/**
 * SettingsToggleRow
 *
 * Reusable full-width settings row: a label (+ optional description) pushed to
 * the left and a Mantine `<Switch>` flush to the right edge (`space-between`).
 * The canonical "settings list row" layout used across forms and panels — use
 * this instead of an inline `<Switch label=... labelPosition="left">`, which
 * does not push the control to the container edge.
 *
 * @module components/common/SettingsToggleRow
 */
import { memo } from 'react';
import { Switch, Text } from '@mantine/core';

import styles from './SettingsToggleRow.module.css';

interface SettingsToggleRowProps {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}

export const SettingsToggleRow = memo(function SettingsToggleRow({
  label,
  description,
  checked,
  onChange,
  disabled,
}: SettingsToggleRowProps) {
  return (
    <div className={styles.row}>
      <div className={styles.labels}>
        <Text size="sm" fw={500}>
          {label}
        </Text>
        {description && (
          <Text size="xs" c="dimmed">
            {description}
          </Text>
        )}
      </div>
      <Switch
        checked={checked}
        disabled={disabled}
        aria-label={label}
        onChange={(event) => onChange(event.currentTarget.checked)}
      />
    </div>
  );
});
